import useSEO from '@/hooks/useSEO';
import { swapFAQs } from './data';
import SwapBasicsSection from './components/SwapBasicsSection';
import SwapCalculationSection from './components/SwapCalculationSection';
import SwapImpactSection from './components/SwapImpactSection';
import TripleSwapSection from './components/TripleSwapSection';
import BrokerSwapComparisonSection from './components/BrokerSwapComparisonSection';
import SwapFAQSection from './components/SwapFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function ForexSwap() {
  useSEO({
    /* @replace:swap-title */

    title: 'Forex Swap Explained: Rollover Rates and Triple Swap',
    /* @replace:swap-description */

    description: 'Learn how forex swap and rollover work, why long and short rates differ, when triple swap applies, and how to calculate overnight holding costs.',
    /* @replace:swap-keywords */

    keywords: 'forex swap, rollover fee, overnight financing, triple swap, swap rate calculation, swap-free account',
    /* @replace:swap-canonical */

    canonical: '/forex-swap/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: swapFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950">
        {/* Decorative: dot grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--secondary-500)) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />

        {/* Decorative: concentric rings top-right — clock/interval motif */}
        <div className="absolute -top-[180px] -right-[180px] w-[560px] h-[560px] rounded-full border border-secondary-500/12" />
        <div className="absolute -top-[120px] -right-[120px] w-[440px] h-[440px] rounded-full border border-secondary-500/10" />
        <div className="absolute -top-[70px] -right-[70px] w-[340px] h-[340px] rounded-full border border-secondary-500/7" />
        <div className="absolute -top-[30px] -right-[30px] w-[260px] h-[260px] rounded-full border border-secondary-500/5" />

        {/* Decorative: concentric rings bottom-left */}
        <div className="absolute -bottom-[140px] -left-[140px] w-[480px] h-[480px] rounded-full border border-accent-500/8" />
        <div className="absolute -bottom-[80px] -left-[80px] w-[360px] h-[360px] rounded-full border border-accent-500/6" />
        <div className="absolute -bottom-[30px] -left-[30px] w-[240px] h-[240px] rounded-full border border-accent-500/4" />

        {/* Decorative: diagonal accent lines — like clock hands */}
        <div className="absolute top-[100px] -left-4 w-[180px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/25 to-secondary-500/0 rotate-[35deg]" />
        <div className="absolute top-[170px] -left-8 w-[140px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/18 to-secondary-500/0 rotate-[55deg]" />
        <div className="absolute top-[60px] right-[15%] w-[120px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/15 to-accent-500/0 -rotate-[25deg]" />

        {/* Decorative: dot constellations — scattered like stars */}
        <div className="absolute top-[30%] left-[6%] flex gap-1.5">
          <span className="w-1 h-1 rounded-full bg-secondary-500/20" />
          <span className="w-1.5 h-1.5 rounded-full bg-secondary-500/30" />
          <span className="w-1 h-1 rounded-full bg-secondary-500/15" />
        </div>
        <div className="absolute bottom-[25%] right-[8%] flex gap-1.5">
          <span className="w-2 h-2 rounded-full bg-accent-500/20" />
          <span className="w-1 h-1 rounded-full bg-accent-500/25" />
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500/15" />
          <span className="w-1 h-1 rounded-full bg-accent-500/18" />
        </div>
        <div className="absolute top-[55%] right-[30%] flex gap-1">
          <span className="w-1 h-1 rounded-full bg-secondary-500/18" />
          <span className="w-1 h-1 rounded-full bg-secondary-500/22" />
          <span className="w-1 h-1 rounded-full bg-secondary-500/14" />
        </div>

        {/* Decorative: thin vertical bars — progress/interval markers */}
        <div className="absolute top-1/4 right-[6%] flex gap-2.5">
          <div className="w-[1px] h-12 bg-secondary-500/12" />
          <div className="w-[1px] h-20 bg-secondary-500/18" />
          <div className="w-[1px] h-8 bg-secondary-500/8" />
          <div className="w-[1px] h-16 bg-secondary-500/14" />
        </div>
        <div className="absolute bottom-1/3 left-[4%] flex gap-2.5">
          <div className="w-[1px] h-16 bg-accent-500/10" />
          <div className="w-[1px] h-10 bg-accent-500/14" />
          <div className="w-[1px] h-22 bg-accent-500/8" />
        </div>

        {/* Decorative: large soft glow behind center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full opacity-[0.06] blur-[130px] bg-secondary-500" />

        {/* Decorative: scattered tiny crosses */}
        <span className="absolute top-[28%] right-[22%] text-secondary-500/12 text-[18px] font-light select-none">+</span>
        <span className="absolute bottom-[22%] left-[18%] text-accent-500/10 text-[16px] font-light select-none">+</span>
        <span className="absolute top-[50%] left-[20%] text-secondary-500/8 text-[14px] font-light select-none">+</span>
        <span className="absolute bottom-[35%] right-[15%] text-accent-500/8 text-[14px] font-light select-none">+</span>

        {/* Main content */}
        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-14 md:pt-16 pb-14 md:pb-16 text-center">
          {/* Heading */}
          <h1 className="text-[40px] md:text-[58px] font-[750] text-background-50 leading-[1.08] tracking-tight mb-5">
            What is
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">forex swap</span>
              <span className="absolute left-0 right-0 bottom-[2px] h-[5px] rounded-full bg-secondary-500/60" />
            </span>
            {' '}(rollover)?
          </h1>

          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[620px] mx-auto mb-7">
            The cost of holding overnight. Invisible to day traders, unavoidable for swing traders. The interest rate gap between two currencies, paid or earned daily.
          </p>

          {/* Summary card */}
          <div className="max-w-[860px] mx-auto bg-foreground-900/80 border border-foreground-800/60 rounded-[20px] md:rounded-[24px] p-5 md:p-9">
            {/* Formula */}
            <div className="text-[22px] md:text-[32px] font-[750] text-background-50 leading-tight tracking-tight text-center mb-5 md:mb-7">
              Swap = Interest rate differential × Position size × Days held
            </div>

            {/* Mobile: compact scenario cards */}
            <div className="md:hidden space-y-3">
              {/* Long */}
              <div className="rounded-xl bg-foreground-800/30 border border-foreground-700/20 p-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1 h-4 rounded-full bg-secondary-400" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-secondary-400">
                    Long Position
                  </span>
                </div>
                <p className="text-[14px] leading-relaxed text-foreground-300">
                  You buy the <strong className="text-background-50 font-semibold">higher-yielding</strong> currency
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-500/10 border border-secondary-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary-400" />
                  <span className="text-[13px] font-semibold text-secondary-400">You earn swap</span>
                </div>
              </div>

              {/* Short */}
              <div className="rounded-xl bg-foreground-800/30 border border-foreground-700/20 p-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1 h-4 rounded-full bg-primary-400" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-primary-400">
                    Short Position
                  </span>
                </div>
                <p className="text-[14px] leading-relaxed text-foreground-300">
                  You buy the <strong className="text-background-50 font-semibold">lower-yielding</strong> currency
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                  <span className="text-[13px] font-semibold text-primary-400">You pay swap</span>
                </div>
              </div>

              {/* Wednesday */}
              <div className="rounded-xl bg-foreground-800/30 border border-foreground-700/20 p-4 text-left">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-1 h-4 rounded-full bg-accent-400" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-accent-400">
                    Wednesday Rule
                  </span>
                </div>
                <p className="text-[14px] leading-relaxed text-foreground-300">
                  Positions held through <strong className="text-background-50 font-semibold">Wednesday 5 PM EST</strong>
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
                  <span className="text-[13px] font-semibold text-accent-400">3× swap charged</span>
                </div>
              </div>
            </div>

            {/* Desktop: three-column grid */}
            <div className="hidden md:grid grid-cols-3 gap-0 divide-x divide-foreground-800/50">
              {/* Long */}
              <div className="px-6 text-center">
                <div className="text-[12px] font-bold uppercase tracking-[0.15em] text-secondary-400 mb-3">
                  Long Position
                </div>
                <p className="text-[15px] leading-relaxed text-foreground-300">
                  You buy the <strong className="text-background-50 font-semibold">higher-yielding</strong> currency
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-500/10 border border-secondary-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-secondary-400" />
                  <span className="text-[14px] font-semibold text-secondary-400">You earn swap</span>
                </div>
              </div>

              {/* Short */}
              <div className="px-6 text-center">
                <div className="text-[12px] font-bold uppercase tracking-[0.15em] text-primary-400 mb-3">
                  Short Position
                </div>
                <p className="text-[15px] leading-relaxed text-foreground-300">
                  You buy the <strong className="text-background-50 font-semibold">lower-yielding</strong> currency
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                  <span className="text-[14px] font-semibold text-primary-400">You pay swap</span>
                </div>
              </div>

              {/* Wednesday */}
              <div className="px-6 text-center">
                <div className="text-[12px] font-bold uppercase tracking-[0.15em] text-accent-400 mb-3">
                  Wednesday Rule
                </div>
                <p className="text-[15px] leading-relaxed text-foreground-300">
                  Positions held through <strong className="text-background-50 font-semibold">Wednesday 5 PM EST</strong>
                </p>
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500/10 border border-accent-500/20">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
                  <span className="text-[14px] font-semibold text-accent-400">3× swap charged</span>
                </div>
              </div>
            </div>

            {/* Bottom note */}
            <div className="mt-5 md:mt-7 pt-4 md:pt-6 border-t border-foreground-800/40">
              <p className="text-[12px] md:text-[13px] leading-relaxed text-foreground-500 text-center">
                Swap is calculated daily at <strong className="text-foreground-400">5:00 PM EST (New York close)</strong>. Wednesday positions cover the weekend, so 3 days of swap are applied at once.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SwapBasicsSection />
      <SwapCalculationSection />
      <SwapImpactSection />
      <TripleSwapSection />
      <BrokerSwapComparisonSection />
      <SwapFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}